import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';

import { fetchCustomerDetail } from '#/api/crmMgmt/customer/fetchDetail';
import {
  fetchCustomerSelectOptions,
  mergeCustomerSelectOption,
} from '#/api/crmMgmt/customer/fetchSelectOptions';
import { createSignReceipt } from '#/api/aftersalesMgmt/signReceipt/create';
import { fetchSignReceiptDetail } from '#/api/aftersalesMgmt/signReceipt/fetchDetail';
import { fetchSignReceiptNextNo } from '#/api/aftersalesMgmt/signReceipt/fetchNextNo';
import { updateSignReceipt } from '#/api/aftersalesMgmt/signReceipt/update';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import { Button } from '#/modules/commonComponents/button';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import LookupRefreshField from '#/modules/commonComponents/form/LookupRefreshField';
import FloatingSelect from '#/modules/commonComponents/form/select';
import AftersalesOpsPageHeader from '#/modules/crmMgmt/aftersales/components/AftersalesOpsPageHeader';
import { getCustomerApiErrorMessage } from '#/modules/crmMgmt/customer/helper/apiError';
import { mapCustomerContactSelectOptions } from '#/modules/crmMgmt/customer/helper/customerAddress';
import SignReceiptItemsEditor from '#/modules/crmMgmt/aftersales/signReceipt/components/SignReceiptItemsEditor';
import SignReceiptStatusBadge from '#/modules/crmMgmt/aftersales/signReceipt/components/SignReceiptStatusBadge';
import {
  getSignReceiptApiErrorMessage,
  isSignReceiptNoDuplicated,
  isSignReceiptVersionConflict,
} from '#/modules/crmMgmt/aftersales/signReceipt/helper/apiError';
import {
  buildSignReceiptWritePayload,
  mapDetailLinesToSelectedItems,
  mapSignReceiptDetailToFormValues,
} from '#/modules/crmMgmt/aftersales/signReceipt/helper/formSerialize';
import {
  EMPTY_SIGN_RECEIPT_CUSTOMER_SNAPSHOT,
  mapCustomerDetailToSignReceiptSnapshot,
} from '#/modules/crmMgmt/aftersales/signReceipt/helper/mapCustomerDetailToSignReceiptSnapshot';
import { canEditSignReceipt } from '#/modules/crmMgmt/aftersales/signReceipt/helper/signReceiptStatus';
import {
  SIGN_RECEIPT_FIELD_LABELS,
  signReceiptFormSchema,
} from '#/modules/crmMgmt/aftersales/signReceipt/schemas/signReceiptFormSchema';
import PageShell from '#/modules/infra/layout/PageShell';
import { toast } from '#/utils/toast';
import {
  lookupQueryKey,
  signReceiptDetailQueryKey,
  signReceiptListQueryKeyPrefix,
} from '#/utils/queryKeys';

const DEFAULT_VALUES = {
  signReceiptNo: '',
  signReceiptDate: '',
  customerId: '',
  contactId: '',
  customerName: '',
  contactName: '',
  phone: '',
  fax: '',
  taxId: '',
  address: '',
  remark: '',
  repairItemIds: [],
};

const SIGN_RECEIPT_SNAPSHOT_FIELD_KEYS = [
  'customerName',
  'contactName',
  'phone',
  'fax',
  'taxId',
  'address',
];

function clearSignReceiptCustomerSnapshot(setValue) {
  setValue('contactId', '', { shouldDirty: true, shouldValidate: true });
  SIGN_RECEIPT_SNAPSHOT_FIELD_KEYS.forEach((key) => {
    setValue(key, EMPTY_SIGN_RECEIPT_CUSTOMER_SNAPSHOT[key] ?? '', {
      shouldDirty: true,
      shouldValidate: true,
    });
  });
}

function applySignReceiptCustomerSnapshot(setValue, snapshot) {
  SIGN_RECEIPT_SNAPSHOT_FIELD_KEYS.forEach((key) => {
    setValue(key, snapshot[key] ?? '', {
      shouldDirty: true,
      shouldValidate: true,
    });
  });
}

const CreateEditSignReceiptPage = () => {
  const { id } = useParams();
  const updatedAtRef = useRef(null);
  const customerIdRef = useRef('');
  /** 載入時的 customer_id；編輯只在變更時送出。 */
  const originalCustomerIdRef = useRef('');
  const customerDetailRef = useRef(null);
  const [pickerItems, setPickerItems] = useState(null);
  const [clearedDetailKey, setClearedDetailKey] = useState(null);
  const [isCustomerDetailLoading, setIsCustomerDetailLoading] = useState(false);
  const [customerContacts, setCustomerContacts] = useState([]);
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: signReceiptDetailQueryKey(id),
    queryFn: () => fetchSignReceiptDetail(id),
    enabled: Boolean(id),
  });

  const customerOptionsQuery = useQuery({
    queryKey: lookupQueryKey('customerSelect'),
    queryFn: fetchCustomerSelectOptions,
  });

  const nextNoQuery = useQuery({
    queryKey: ['erp', 'signReceipt', 'nextNo'],
    queryFn: () => fetchSignReceiptNextNo(),
    enabled: !id,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const suggestedNo = typeof nextNoQuery.data?.data === 'string'
    ? nextNoQuery.data.data
    : '';

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(signReceiptFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const watchedCustomerId = watch('customerId');

  const linkedCustomer = detailQuery.data?.data?.customer;
  const customerOptions = useMemo(() => {
    const options = mergeCustomerSelectOption(customerOptionsQuery.data ?? [], linkedCustomer);
    return [{ value: '', label: '請選擇' }, ...options];
  }, [customerOptionsQuery.data, linkedCustomer]);

  const contactOptions = useMemo(() => {
    const options = mapCustomerContactSelectOptions(customerContacts);
    return [{ value: '', label: '請選擇' }, ...options];
  }, [customerContacts]);

  const detail = detailQuery.data?.data;
  const detailKey = detail
    ? `${detail.id}:${detail.record?.updatedAt ?? ''}`
    : null;
  const detailItems = detail ? mapDetailLinesToSelectedItems(detail) : [];

  if (detailKey && detailKey !== clearedDetailKey) {
    setClearedDetailKey(detailKey);
    setPickerItems(null);
    setCustomerContacts([]);
    customerIdRef.current = '';
    customerDetailRef.current = null;
  }

  const selectedItems = pickerItems ?? detailItems;

  async function refreshNextNo() {
    const result = await nextNoQuery.refetch();
    const next = typeof result.data?.data === 'string' ? result.data.data : '';
    if (next) {
      setValue('signReceiptNo', next, { shouldValidate: true });
    }
    return next;
  }

  async function createWithDocumentNo(payload) {
    try {
      return await createSignReceipt(payload);
    } catch (error) {
      if (isSignReceiptNoDuplicated(error)) {
        await refreshNextNo();
        toast.info('單號已存在，已重新帶入建議單號，請確認後再儲存（勿自動重送）');
      }
      throw error;
    }
  }

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    createFn: createWithDocumentNo,
    updateFn: updateSignReceipt,
    buildPayload: (values) => buildSignReceiptWritePayload(values, {
      updatedAt: id ? updatedAtRef.current : null,
      isEdit: Boolean(id),
      originalCustomerId: originalCustomerIdRef.current,
    }),
    listQueryKeyPrefix: signReceiptListQueryKeyPrefix,
    detailQueryKey: signReceiptDetailQueryKey,
    getDetailPath: ROUTES.signReceiptDetail,
    getErrorMessage: (error) => getSignReceiptApiErrorMessage(error, '儲存失敗'),
    isVersionConflict: isSignReceiptVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  useEffect(() => {
    if (!detail) {
      return;
    }

    updatedAtRef.current = detail.record?.updatedAt ?? null;
    const formValues = mapSignReceiptDetailToFormValues(detail);
    customerIdRef.current = formValues.customerId;
    originalCustomerIdRef.current = formValues.customerId;
    resetValidationSummary();
    reset(formValues);

    if (!formValues.customerId) {
      return undefined;
    }
    // 已關聯客戶：只載入聯絡人選項，不覆蓋單據快照
    let cancelled = false;
    fetchCustomerDetail(formValues.customerId)
      .then((result) => {
        const customerDetail = result?.data ?? null;
        if (cancelled || customerIdRef.current !== formValues.customerId) {
          return;
        }
        customerDetailRef.current = customerDetail;
        setCustomerContacts(customerDetail?.contacts ?? []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [detail, reset, resetValidationSummary]);

  useEffect(() => {
    if (id || !suggestedNo) {
      return;
    }
    setValue('signReceiptNo', suggestedNo, { shouldValidate: false });
  }, [id, suggestedNo, setValue]);

  async function handleCustomerChange(nextId) {
    const next = nextId ?? '';
    const prev = customerIdRef.current ?? '';
    if (next === prev) {
      return;
    }

    customerIdRef.current = next;
    customerDetailRef.current = null;
    setCustomerContacts([]);
    clearSignReceiptCustomerSnapshot(setValue);

    if (!next) {
      return;
    }

    setIsCustomerDetailLoading(true);
    try {
      const result = await fetchCustomerDetail(next);
      const customerDetail = result?.data ?? null;
      customerDetailRef.current = customerDetail;
      setCustomerContacts(customerDetail?.contacts ?? []);
    } catch (error) {
      toast.error(getCustomerApiErrorMessage(error, '載入客戶資料失敗'));
    } finally {
      setIsCustomerDetailLoading(false);
    }
  }

  function handleContactChange(nextContactId) {
    const contactId = nextContactId ?? '';
    if (!contactId) {
      clearSignReceiptCustomerSnapshot(setValue);
      return;
    }

    const snapshot = mapCustomerDetailToSignReceiptSnapshot(
      customerDetailRef.current,
      contactId,
    );
    applySignReceiptCustomerSnapshot(setValue, snapshot);
  }

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    if (!isEdit && !String(values.signReceiptNo ?? '').trim()) {
      toast.error('請先取得或填寫簽收單號');
      return;
    }
    save(values);
  }

  const pageTitle = isEdit ? '編輯簽收單' : '新增簽收單';
  const isPageLoading = isEdit && detailQuery.isLoading;
  const isDetailNotFound = isEdit && detailQuery.isError;
  const isReadOnlyEdit = isEdit && detail && !canEditSignReceipt(detail.status);
  const isNextNoLoading = !isEdit && nextNoQuery.isLoading;
  const isNextNoError = !isEdit && nextNoQuery.isError;
  const canShowForm = !isPageLoading && !isDetailNotFound && !isReadOnlyEdit;
  const hasCustomer = Boolean(watchedCustomerId);
  const isContactSelectDisabled = !hasCustomer || isCustomerDetailLoading;

  const headerTrailing = isEdit && id ? (
    <Button type="button" variant="outline" asChild>
      <Link to={ROUTES.signReceiptDetail(id)}>返回詳情</Link>
    </Button>
  ) : null;

  const documentFields = (
    <section className="pb-6">
      <div className="form-block">
        <h2 className="form-block__title">單據資訊</h2>
      </div>
      <div className="grid grid-cols-1 gap-3">
        <div
          className={cn(
            'grid grid-cols-1 gap-3',
            '@xl/sheet:grid-cols-2',
          )}
        >
          {!isEdit ? (
            <div className="space-y-2">
              {isNextNoLoading ? (
                <p className="text-sm text-slate-500">取得建議單號中…</p>
              ) : null}
              {isNextNoError ? (
                <p className="text-sm text-destructive">
                  {getSignReceiptApiErrorMessage(
                    nextNoQuery.error,
                    '取得建議單號失敗',
                  )}
                </p>
              ) : null}
              <LookupRefreshField
                onRefresh={() => refreshNextNo()}
                isRefreshing={nextNoQuery.isFetching}
                ariaLabel={
                  isNextNoError ? '重新取得簽收單號' : '重新建議簽收單號'
                }
              >
                <FloatingInput
                  id="signReceiptNo"
                  label="簽收單號"
                  register={register}
                  errors={errors}
                />
              </LookupRefreshField>
            </div>
          ) : (
            <FloatingInput
              id="signReceiptNo"
              label="簽收單號"
              value={detail?.signReceiptNo ?? ''}
              readOnly
            />
          )}
          <FloatingDateInput
            id="signReceiptDate"
            label="預計送交日"
            control={control}
            errors={errors}
            placeholder="西元年/月/日"
          />
        </div>

        <div
          className={cn(
            'grid grid-cols-1 gap-3',
            '@xl/sheet:grid-cols-2',
            '@4xl/sheet:grid-cols-3',
          )}
        >
          <FloatingInput
            id="taxId"
            label="統一編號"
            register={register}
            errors={errors}
            containerClassName="@xl/sheet:col-span-2 @4xl/sheet:col-span-1"
          />
          <FloatingSelect
            id="customerId"
            label="客戶"
            options={customerOptions}
            control={control}
            errors={errors}
            isDisabled={customerOptionsQuery.isLoading || isCustomerDetailLoading}
            noOptionsMessage="無可用客戶"
            onChange={handleCustomerChange}
          />
          <FloatingSelect
            id="contactId"
            label="帶入聯絡人"
            options={contactOptions}
            control={control}
            errors={errors}
            isDisabled={isContactSelectDisabled}
            noOptionsMessage={hasCustomer ? '此客戶尚無聯絡人' : '請先選擇客戶'}
            onChange={handleContactChange}
          />
        </div>

        <div
          className={cn(
            'grid grid-cols-1 gap-3',
            '@xl/sheet:grid-cols-2',
            '@4xl/sheet:grid-cols-4',
          )}
        >
          <FloatingInput
            id="customerName"
            label="客戶名稱"
            register={register}
            errors={errors}
          />
          <FloatingInput
            id="contactName"
            label="聯絡人"
            register={register}
            errors={errors}
          />
          <FloatingInput
            id="phone"
            label="電話"
            register={register}
            errors={errors}
          />
          <FloatingInput
            id="fax"
            label="傳真"
            register={register}
            errors={errors}
          />
          <FloatingInput
            id="address"
            label="地址"
            register={register}
            errors={errors}
            containerClassName="col-span-1 @xl/sheet:col-span-2 @4xl/sheet:col-span-4"
          />
          <FloatingTextarea
            id="remark"
            label="備註"
            register={register}
            errors={errors}
            containerClassName="col-span-1 @xl/sheet:col-span-2 @4xl/sheet:col-span-4"
          />
        </div>
      </div>
    </section>
  );

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-6xl">
          <AftersalesOpsPageHeader
            page="signReceipt"
            variant="form"
            title={pageTitle}
            statusSlot={
              isEdit && detail?.status
                ? <SignReceiptStatusBadge status={detail.status} />
                : null
            }
            trailing={headerTrailing}
          />

          {isPageLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getSignReceiptApiErrorMessage(detailQuery.error, '找不到簽收單')}
            </p>
          ) : null}

          {isReadOnlyEdit ? (
            <div className="space-y-3">
              <p className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                <span>僅草稿可編輯。目前狀態為</span>
                <SignReceiptStatusBadge status={detail.status} size="sm" />
                <span>，請至詳情頁操作。</span>
              </p>
              <Button type="button" asChild>
                <Link to={ROUTES.signReceiptDetail(id)}>返回詳情</Link>
              </Button>
            </div>
          ) : null}

          {canShowForm ? (
            <>
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={SIGN_RECEIPT_FIELD_LABELS}
                customMessages={customMessages}
                className="mt-5"
              />
              <form
                className="sheet create_edit create_edit_form create_edit_form--page-scroll"
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body divide-y divide-gray-500">
                  {documentFields}
                  <SignReceiptItemsEditor
                    control={control}
                    setValue={setValue}
                    selectedItems={selectedItems}
                    onSelectedItemsChange={setPickerItems}
                    errors={errors}
                  />
                </div>
                <div className="create_edit_form__actions flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link
                      to={isEdit && id ? ROUTES.signReceiptDetail(id) : ROUTES.signReceipt}
                    >
                      取消
                    </Link>
                  </Button>
                  <Button
                    type="submit"
                    disabled={isPending || (!isEdit && isNextNoLoading)}
                  >
                    {isPending ? '儲存中…' : '儲存'}
                  </Button>
                </div>
              </form>
            </>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditSignReceiptPage;
